import type { MenuItemId } from '@/systems/MenuNavigation';
import { type PauseMenuState, type VolumeKey } from '@/systems/PauseMenuState';
import type { GameOptions } from '@/systems/SaveManager';
import { getAudioBus } from '@/systems/AudioBus';

export const OPTION_VOLUME_STEP = 0.1;

const OPT_VOLUME_KEYS: Partial<Record<MenuItemId, VolumeKey>> = {
  'opt-master': 'masterVolume',
  'opt-music': 'musicVolume',
  'opt-sfx': 'sfxVolume',
  'opt-voice': 'voiceVolume',
};

export interface OptionActionResult {
  readonly changed: boolean;
  readonly audioChanged: boolean;
  readonly back: boolean;
  readonly notification?: string;
}

const NO_CHANGE: OptionActionResult = { changed: false, audioChanged: false, back: false };

/** Volume [0.0-1.0] -> display integer string "0"-"10". */
function fmtVol(v: number): string {
  return String(Math.round(v * 10));
}

export function optionValueText(id: MenuItemId, opts: Readonly<GameOptions>): string {
  switch (id) {
    case 'opt-master':
      return `◀ ${fmtVol(opts.masterVolume ?? 1.0)} ▶`;
    case 'opt-mute':
      return opts.masterMuted ? '[ON]' : '[OFF]';
    case 'opt-music':
      return `◀ ${fmtVol(opts.musicVolume)} ▶`;
    case 'opt-sfx':
      return `◀ ${fmtVol(opts.sfxVolume)} ▶`;
    case 'opt-voice':
      return `◀ ${fmtVol(opts.voiceVolume)} ▶`;
    case 'opt-blood':
      return opts.bloodEnabled ? '[ON]' : '[OFF]';
    case 'opt-shake':
      return opts.screenShakeEnabled ? '[ON]' : '[OFF]';
    case 'opt-language':
      return `[ ${opts.language.toUpperCase()} ]`;
    case 'opt-key-remap':
    case 'opt-gamepad-remap':
      return '[ENTER]';
    case 'opt-back':
    default:
      return '';
  }
}

export function applyOptionConfirm(
  state: PauseMenuState,
  id: MenuItemId | null,
): OptionActionResult {
  switch (id) {
    case 'opt-mute':
      state.toggleMasterMute();
      return { changed: true, audioChanged: true, back: false };
    case 'opt-blood':
      state.toggleBlood();
      return { changed: true, audioChanged: false, back: false };
    case 'opt-shake':
      state.toggleScreenShake();
      return { changed: true, audioChanged: false, back: false };
    case 'opt-language':
      return {
        changed: false,
        audioChanged: false,
        back: false,
        notification: 'ENGLISH ONLY IN THIS BUILD',
      };
    case 'opt-key-remap':
      return {
        changed: false,
        audioChanged: false,
        back: false,
        notification: 'KEY REMAP — NOT YET AVAILABLE',
      };
    case 'opt-gamepad-remap':
      return {
        changed: false,
        audioChanged: false,
        back: false,
        notification: 'GAMEPAD REMAP — NOT YET AVAILABLE',
      };
    case 'opt-back':
      return { changed: false, audioChanged: false, back: true };
    case null:
      return NO_CHANGE;
    default:
      return adjustOption(state, id, OPTION_VOLUME_STEP);
  }
}

export function adjustOption(
  state: PauseMenuState,
  id: MenuItemId,
  delta: number,
): OptionActionResult {
  const volKey = OPT_VOLUME_KEYS[id];
  if (volKey !== undefined) {
    state.nudgeVolume(volKey, delta);
    return { changed: true, audioChanged: true, back: false };
  }
  if (id === 'opt-mute') {
    state.toggleMasterMute();
    return { changed: true, audioChanged: true, back: false };
  }
  if (id === 'opt-blood') {
    state.toggleBlood();
    return { changed: true, audioChanged: false, back: false };
  }
  if (id === 'opt-shake') {
    state.toggleScreenShake();
    return { changed: true, audioChanged: false, back: false };
  }
  return NO_CHANGE;
}

/** Push current options to the shared AudioBus for immediate audio effect. */
export function applyLiveAudio(opts: Readonly<GameOptions>): void {
  const bus = getAudioBus();
  bus.setVolume('master', opts.masterVolume ?? 1.0);
  bus.setVolume('music', opts.musicVolume);
  bus.setVolume('sfx', opts.sfxVolume);
  bus.setMute('master', opts.masterMuted ?? false);
}
